"use client";

import { PlainMessage } from "@bufbuild/protobuf";
import {
  EventSeriesDetails,
  Section,
} from "@egocentric-systems/ts-apis/booking_gateway/v1/events_pb";
import { ReactNode, useMemo, useState } from "react";
import {
  AccordionItem,
  AccordionContent,
  AccordionTrigger,
  Accordion,
} from "~/components/ui/accordion";
import { cn } from "~/lib/utils";
import { Price } from "./Price";
import { AnimatePresence, motion } from "framer-motion";
import { useCart } from "~/hooks/useCart";
import { useSectionUpdates } from "~/hooks/useSectionUpdates";
import { EventOrBundle } from "~/lib/definitions";
import { useFilteredSections } from "~/app/tickets/[eventId]/_components/useFilteredSections";
import showdown from "showdown";
import { SoldOutTag } from "~/app/tickets/[eventId]/_components/SoldOutTag";
import { UnlockBadge } from "~/app/tickets/[eventId]/_components/UnlockBadge";
import { isDtmShop } from "~/lib/client/isDtmShop";

type ItemProps = {
  section: PlainMessage<Section>;
  eventOrBundle: EventOrBundle;
  seriesDetails: PlainMessage<EventSeriesDetails>;
  ticketsAvailableText: string;
  showTicketAvailability: boolean;
  updates: {
    [eventId: string]: {
      [sectionId: string]: number;
    };
  };
};

function TicketItem(props: ItemProps): ReactNode {
  const {
    section,
    eventOrBundle,
    seriesDetails,
    ticketsAvailableText,
    showTicketAvailability,
    updates,
  } = props;

  const htmlDescription = useMemo(() => {
    return new showdown.Converter().makeHtml(section.description);
  }, [section.description]);

  const availability = Object.values(updates).reduce(
    (minAvailability: number | undefined, eventUpdate) => {
      const sectionAvailability = eventUpdate[section.id];

      if (minAvailability === undefined || sectionAvailability === undefined) {
        return undefined;
      }

      return Math.min(minAvailability, sectionAvailability);
    },
    Infinity,
  );

  const color: "success" | "orange" | "black" | "transparent" = useMemo(() => {
    if (availability === undefined) {
      if (section.capacity > 10) {
        return "success";
      }
      if (section.capacity > 0) {
        return "orange";
      }
      return "black";
    }

    if (availability === Infinity) {
      return "transparent";
    }

    if (availability > 10) {
      return "success";
    }
    if (availability > 0) {
      return "orange";
    }
    return "black";
  }, [availability, section.capacity]);

  const someUnlockedPrices = section.prices?.some((price) => price.isUnlocked);

  return (
    <AccordionItem value={section.id} key={section.id}>
      <AccordionTrigger
        iconInFront
        className={cn(
          "h-auto w-full whitespace-normal bg-gray-100 px-[16px] py-[12px] hover:no-underline",
        )}
      >
        <div className="flex w-full grow flex-row justify-between gap-[8px]">
          <div className="flex w-full flex-col">
            <div className="flex flex-row gap-2">
              {someUnlockedPrices && <UnlockBadge type="section" />}
              <p className="grow text-left text-normal font-bold leading-md text-text">
                {section.name}
              </p>
            </div>
            <div>
              {showTicketAvailability && (
                <div className="mt-2 flex w-full flex-row items-center justify-start gap-[8px]">
                  <motion.div
                    className={cn(
                      "h-[6px] w-[6px] rounded-full",
                      color === "success" && "bg-success",
                      color === "orange" && "bg-orange1",
                      color === "black" && "bg-black opacity-50",
                    )}
                    key={section.capacity}
                    animate={{ scale: [1.35, 1] }}
                    transition={{ duration: 0.25 }}
                  />
                  {availability !== Infinity && (
                    <p className="text-left text-[14px] text-black">
                      {availability ?? section.capacity} {ticketsAvailableText}
                    </p>
                  )}
                </div>
              )}
              <div
                dangerouslySetInnerHTML={{ __html: htmlDescription }}
                className="pt-[4px] text-left text-[12px] text-gray-400"
              />
            </div>
          </div>
          <div>{Number(availability) <= 0 && <SoldOutTag />}</div>
        </div>
      </AccordionTrigger>
      <AccordionContent>
        {section?.prices?.map((price, index) => (
          <Price
            section={section}
            price={price}
            key={price.id}
            eventOrBundle={eventOrBundle}
            series={seriesDetails?.series}
            index={index}
            length={section.prices.length}
            availability={availability}
          />
        ))}
      </AccordionContent>
    </AccordionItem>
  );
}

type Props = {
  sections: PlainMessage<Section>[];
  eventOrBundle: EventOrBundle;
  seriesDetails: PlainMessage<EventSeriesDetails>;
  showAvailability: boolean;
  ticketsAvailableText: string;
};

export default function Tickets({
  sections,
  eventOrBundle,
  seriesDetails,
  ticketsAvailableText,
  showAvailability,
}: Props): ReactNode {
  const [value, setValue] = useState<string[]>(
    isDtmShop() ? sections.map((section) => section.id) : [sections[0].id],
  );
  const filtered = useFilteredSections(sections);

  const updates = useSectionUpdates({
    disabled: !showAvailability,
    eventOrBundle,
  });

  return (
    <AnimatePresence>
      <motion.div
        exit="hidden"
        initial="hidden"
        animate="visible"
        variants={{
          hidden: {},
          visible: { transition: { staggerChildren: 0.1 } },
        }}
      >
        <Accordion value={value} type="multiple" onValueChange={setValue}>
          {filtered.map((section) => (
            <motion.div
              layout
              exit="exit"
              key={section.id}
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 0, y: -10 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1] },
                },
                exit: {
                  opacity: 0,
                  y: 10,
                  transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
                },
              }}
            >
              <TicketItem
                showTicketAvailability={showAvailability}
                updates={updates}
                ticketsAvailableText={ticketsAvailableText}
                key={section.id}
                seriesDetails={seriesDetails}
                eventOrBundle={eventOrBundle}
                section={section}
              />
            </motion.div>
          ))}
        </Accordion>
      </motion.div>
    </AnimatePresence>
  );
}
